import { useMemo } from 'react';
import { useStableState } from '@direct-frontend/stdlib';
import { type GridRowSelectionManager } from '@adv-frontend/grid';
import { showSuccess } from '../../../lib/toast';

const MAX_SELECTED = 500;

export class SelectionSet<T> extends Set<T> {
    allSelected: boolean = false;

    excludedSet: Set<T> = new Set();

    addExcluded(value: T) {
        this.excludedSet.add(value);
    }

    deleteExcluded(value: T) {
        this.excludedSet.delete(value);
    }

    hasExcluded(value: T) {
        return this.excludedSet.has(value);
    }
}

/**
 * Состояние с информацией о выделенных строках в таблице
 */
export type GridRowEnhancedSelectionState = {
    /**
     * Геттер для получения актуального значения.
     * @returns Идентификаторы выделенных строк.
     */
    getState: () => SelectionSet<string>;
    /**
     * Хук для подписки на изменение значения, полученного с помощью селектора
     * @params selector Селектор, принимающий на вход стейт выделенных строк
     * @returns Значение, возвращенное селектором
     */
    useSelector: <T>(selector: (state: SelectionSet<string>) => T) => T;
    /**
     * Метод для установки нового значения.
     * @param value Выделенные строки.
     */
    setState: (value: SelectionSet<string>) => void;
    /**
     * Хук для подписки на изменения значения.
     * @returns Идентификаторы доступных для выбора строк.
     */
    useState: () => Set<string>;
};

export type GridRowEnhancedSelectionManager = GridRowSelectionManager & {
    useState: () => SelectionSet<string>;
};

/*
 * Создает менеджер для управления выделением строк в таблице.
 * @returns Менеджер для управления выделением строк в таблице.
 */
export function createGridRowEnhancedSelectionManager({
    state,
}: {
    state: GridRowEnhancedSelectionState;
}): GridRowEnhancedSelectionManager {
    const manager: GridRowEnhancedSelectionManager = {
        isSelected: (id: string) => {
            const currentState = state.getState();

            return (currentState.allSelected && !currentState.hasExcluded(id)) || currentState.has(id);
        },
        useIsSelected: (id: string) =>
            state.useSelector((state) => state.has(id) || (state.allSelected && !state.hasExcluded(id))),
        isSomeRowSelected: (ids: string[]) => {
            const currentState = state.getState();

            return ids.some(
                (id) => currentState.has(id) || (currentState.allSelected && !currentState.hasExcluded(id))
            );
        },
        useIsSomeRowSelected: (ids: string[]) => {
            const currentState = state.useSelector((state) => state);

            return useMemo(
                () =>
                    ids.some(
                        (id) => currentState.has(id) || (currentState.allSelected && !currentState.hasExcluded(id))
                    ),
                [currentState, ids]
            );
        },
        isEveryRowSelected: (ids: string[]) => {
            const currentState = state.getState();

            return ids.every(
                (id) => currentState.has(id) || (currentState.allSelected && !currentState.hasExcluded(id))
            );
        },
        useIsEveryRowSelected: (ids: string[]) => {
            const currentState = state.useSelector((state) => state);

            return useMemo(
                () =>
                    ids.every(
                        (id) => currentState.has(id) || (currentState.allSelected && !currentState.hasExcluded(id))
                    ),
                [currentState, ids]
            );
        },
        isSelectable: (id: string) => {
            const currentState = state.getState();

            if (manager.isSelected(id)) {
                if (currentState.allSelected) {
                    return currentState.excludedSet.size < MAX_SELECTED;
                }

                return true;
            }

            if (currentState.allSelected) {
                return true;
            }

            return currentState.size < MAX_SELECTED;
        },
        useIsSelectable: (id: string) => {
            const currentState = state.useSelector((state) => state);

            return useMemo(() => {
                if (manager.isSelected(id)) {
                    if (currentState.allSelected) {
                        return currentState.excludedSet.size < MAX_SELECTED;
                    }

                    return true;
                }

                if (currentState.allSelected) {
                    return true;
                }

                return currentState.size < MAX_SELECTED;
            }, [currentState, id]);
        },
        selectRow: (id: string) => {
            if (manager.isSelectable(id)) {
                const prev = state.getState();

                if ((!prev.allSelected && prev.has(id)) || (prev.allSelected && !prev.hasExcluded(id))) {
                    return;
                }

                const selectedRows = new SelectionSet(prev);

                selectedRows.allSelected = prev.allSelected;
                selectedRows.excludedSet = new Set(prev.excludedSet);

                if (selectedRows.allSelected) {
                    selectedRows.deleteExcluded(id);
                } else {
                    selectedRows.add(id);
                }

                if (selectedRows.size === MAX_SELECTED) {
                    showSuccess('Можно одновременно выбрать не более 500 товаров', {
                        caption: 'Чтобы работать с большим количеством товаров, используйте фильтры',
                    });
                }

                state.setState(selectedRows);
            }
        },
        deselectRow: (id: string) => {
            if (manager.isSelectable(id)) {
                const prev = state.getState();

                if ((!prev.allSelected && !prev.has(id)) || (prev.allSelected && prev.hasExcluded(id))) {
                    return;
                }

                const selectedRows = new SelectionSet(prev);

                selectedRows.allSelected = prev.allSelected;
                selectedRows.excludedSet = new Set(prev.excludedSet);

                if (selectedRows.allSelected) {
                    selectedRows.addExcluded(id);
                } else {
                    selectedRows.delete(id);
                }

                if (selectedRows.excludedSet.size === MAX_SELECTED) {
                    showSuccess('Можно одновременно снять выделение для не более 500 товаров', {
                        caption: 'Чтобы работать с большим количеством товаров, используйте фильтры',
                    });
                }

                state.setState(selectedRows);
            }
        },
        toggleRow: (id: string) => {
            if (manager.isSelectable(id)) {
                if (manager.isSelected(id)) {
                    manager.deselectRow(id);
                } else {
                    manager.selectRow(id);
                }
            }
        },
        deselectMultipleRows: (ids: string[]) => {
            if (!manager.isSomeRowSelected(ids)) {
                return;
            }

            ids.forEach((id) => {
                manager.deselectRow(id);
            });
        },
        selectMultipleRows: (ids: string[]) => {
            if (manager.isEveryRowSelected(ids)) {
                return;
            }

            ids.forEach((id) => {
                manager.selectRow(id);
            });
        },
        isAllSelected: () => {
            const currentState = state.getState();

            return currentState.allSelected;
        },
        useIsAllSelected: () => state.useSelector((state) => state.allSelected),
        hasSelectableRows: () => {
            const selectableIds = state.getState();

            return selectableIds.size > 0;
        },
        useHasSelectableRows: () => {
            const selectableIds = state.useState();

            return selectableIds.size > 0;
        },
        selectAll: () => {
            const newState = new SelectionSet<string>();

            newState.allSelected = true;
            state.setState(newState);
        },
        deselectAll: () => {
            const newState = new SelectionSet<string>();

            state.setState(newState);
        },
        toggleAll: () => {
            if (manager.isAllSelected?.()) {
                manager.deselectAll();
            } else {
                manager.selectAll?.();
            }
        },
        useState: () => state.useSelector((state) => state),
    };

    return manager;
}

const emptySet = new SelectionSet<string>();

export const useGridRowEnhancedSelectionManager = (
    initialSelectedItems: SelectionSet<string> = emptySet
): GridRowEnhancedSelectionManager => {
    const state = useStableState<SelectionSet<string>>(initialSelectedItems);

    return useMemo(() => createGridRowEnhancedSelectionManager({ state }), [state]);
};
